import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { TopProductBiDto } from "../../../shared/dtos/bi/TopProductBiDto";

interface TopProductsWidgetProps {
  data?: TopProductBiDto[];
}

const TopProductsWidget: React.FC<TopProductsWidgetProps> = ({ data = [] }) => {
  const theme = useTheme();

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Top Performing Products
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="productName"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  tickFormatter={(val) =>
                    val.length > 15 ? `${val.substring(0, 15)}...` : val
                  }
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(value)
                  }
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  contentStyle={{ borderRadius: 8 }}
                  formatter={(value, name) => [
                    formatCurrency(Number(value)),
                    name === "totalRevenue" ? "Revenue" : "Profit",
                  ]}
                />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                <Bar
                  yAxisId="left"
                  dataKey="totalRevenue"
                  name="Revenue"
                  fill={theme.palette.secondary.main}
                  barSize={40}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalProfit"
                  name="Profit"
                  stroke={theme.palette.success.main}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 350,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No product data available.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TopProductsWidget;
